// Property-Unit Relationship Matrix
// Defines valid combinations of property purposes, types, and unit types

import type { Language } from "@/types/i18n";

export const PROPERTY_PURPOSES = [
  { value: "agricultural", labelAr: "زراعي", labelEn: "Agricultural" },
  { value: "residential", labelAr: "سكني", labelEn: "Residential" },
  { value: "commercial", labelAr: "تجاري", labelEn: "Commercial" },
  { value: "industrial", labelAr: "صناعي", labelEn: "Industrial" },
  { value: "medical", labelAr: "صحي", labelEn: "Medical" },
  { value: "educational", labelAr: "تعليمي", labelEn: "Educational" },
  { value: "multi_purpose", labelAr: "متعدد الأغراض", labelEn: "Multi Purpose" },
] as const;

export const PROPERTY_TYPES = [
  // Agricultural
  { value: "land", labelAr: "أرض", labelEn: "Land", purposes: ["agricultural", "residential", "commercial", "industrial", "multi_purpose"] },
  { value: "farm", labelAr: "مزرعة", labelEn: "Farm", purposes: ["agricultural"] },
  // Residential
  { value: "floor", labelAr: "دور", labelEn: "Floor", purposes: ["residential"] },
  { value: "apartment", labelAr: "شقة", labelEn: "Apartment", purposes: ["residential"] },
  { value: "villa", labelAr: "فيلا", labelEn: "Villa", purposes: ["residential"] },
  { value: "studio", labelAr: "استوديو", labelEn: "Studio", purposes: ["residential"] },
  { value: "room", labelAr: "غرفة", labelEn: "Room", purposes: ["residential"] },
  { value: "retreat", labelAr: "استراحة", labelEn: "Retreat", purposes: ["residential"] },
  { value: "compound", labelAr: "مجمع", labelEn: "Compound", purposes: ["residential", "multi_purpose"] },
  { value: "building", labelAr: "عمارة", labelEn: "Building", purposes: ["residential", "commercial", "multi_purpose"] },
  // Commercial
  { value: "commercial_center", labelAr: "مركز تجاري", labelEn: "Commercial Center", purposes: ["commercial", "multi_purpose"] },
  // Industrial
  { value: "factory", labelAr: "مصنع", labelEn: "Factory", purposes: ["industrial"] },
  { value: "power_plant", labelAr: "محطة طاقة", labelEn: "Power Plant", purposes: ["industrial"] },
  { value: "telecom_tower", labelAr: "برج اتصالات", labelEn: "Telecom Tower", purposes: ["industrial"] },
  { value: "gas_station", labelAr: "محطة وقود", labelEn: "Gas Station", purposes: ["industrial"] },
  // Medical
  { value: "health_center", labelAr: "مركز صحي", labelEn: "Health Center", purposes: ["medical"] },
  // Educational
  { value: "school", labelAr: "مدرسة", labelEn: "School", purposes: ["educational"] },
] as const;

export const UNIT_TYPES = [
  // Industrial — land
  { value: "industrial_land", labelAr: "أرض صناعية", labelEn: "Industrial Land", purposes: ["industrial"] },
  // Agricultural
  { value: "agricultural_land", labelAr: "أرض زراعية", labelEn: "Agricultural Land", purposes: ["agricultural"] },
  { value: "farm", labelAr: "مزرعة", labelEn: "Farm", purposes: ["agricultural"] },
  // Residential
  { value: "residential_land", labelAr: "أرض سكنية", labelEn: "Residential Land", purposes: ["residential"] },
  { value: "floor", labelAr: "دور", labelEn: "Floor", purposes: ["residential", "multi_purpose"] },
  { value: "apartment", labelAr: "شقة", labelEn: "Apartment", purposes: ["residential", "multi_purpose"] },
  { value: "villa", labelAr: "فيلا", labelEn: "Villa", purposes: ["residential", "multi_purpose"] },
  { value: "duplex", labelAr: "دوبلكس", labelEn: "Duplex", purposes: ["residential"] },
  { value: "townhouse", labelAr: "تاون هاوس", labelEn: "Townhouse", purposes: ["residential"] },
  { value: "palace", labelAr: "قصر", labelEn: "Palace", purposes: ["residential"] },
  { value: "studio", labelAr: "استوديو", labelEn: "Studio", purposes: ["residential", "multi_purpose"] },
  { value: "room", labelAr: "غرفة", labelEn: "Room", purposes: ["residential", "multi_purpose"] },
  { value: "retreat", labelAr: "استراحة", labelEn: "Retreat", purposes: ["residential"] },
  // Commercial
  { value: "commercial_land", labelAr: "أرض تجارية", labelEn: "Commercial Land", purposes: ["commercial"] },
  { value: "office", labelAr: "مكتب", labelEn: "Office", purposes: ["commercial", "multi_purpose"] },
  { value: "gallery", labelAr: "معرض", labelEn: "Gallery", purposes: ["commercial", "multi_purpose"] },
  { value: "showroom", labelAr: "صالة عرض", labelEn: "Showroom", purposes: ["commercial", "multi_purpose"] },
  { value: "booth", labelAr: "كشك", labelEn: "Booth", purposes: ["commercial", "multi_purpose"] },
  { value: "shop", labelAr: "محل", labelEn: "Shop", purposes: ["commercial", "multi_purpose"] },
  { value: "warehouse", labelAr: "مستودع", labelEn: "Warehouse", purposes: ["commercial", "multi_purpose"] },
  { value: "cinema", labelAr: "سينما", labelEn: "Cinema", purposes: ["commercial", "multi_purpose"] },
  { value: "hotel", labelAr: "فندق", labelEn: "Hotel", purposes: ["commercial", "multi_purpose"] },
  { value: "car_parking", labelAr: "موقف سيارات", labelEn: "Car Parking", purposes: ["commercial", "multi_purpose"] },
  { value: "workshop", labelAr: "ورشة", labelEn: "Workshop", purposes: ["commercial", "multi_purpose"] },
  { value: "atm", labelAr: "صراف آلي", labelEn: "ATM", purposes: ["commercial", "multi_purpose"] },
  // Industrial
  { value: "factory", labelAr: "مصنع", labelEn: "Factory", purposes: ["industrial"] },
  { value: "power_plant", labelAr: "محطة طاقة", labelEn: "Power Plant", purposes: ["industrial"] },
  { value: "telecom_tower", labelAr: "برج اتصالات", labelEn: "Telecom Tower", purposes: ["industrial"] },
  { value: "gas_station", labelAr: "محطة وقود", labelEn: "Gas Station", purposes: ["industrial"] },
  // Medical
  { value: "health_center", labelAr: "مركز صحي", labelEn: "Health Center", purposes: ["medical"] },
  // Educational
  { value: "school", labelAr: "مدرسة", labelEn: "School", purposes: ["educational"] },
] as const;

// Purpose-to-Unit-Types mapping for Add Unit form
// This provides a simplified dropdown filtering based on purpose only
export const UNIT_TYPES_BY_PURPOSE: Record<string, string[]> = {
  residential: ["apartment", "villa", "duplex", "townhouse", "studio"],
  commercial: ["office", "shop", "gallery", "showroom"],
  industrial: ["industrial_land", "warehouse", "workshop", "factory"],
  agricultural: ["agricultural_land", "farm"],
  medical: ["health_center"],
  educational: ["school"],
  multi_purpose: [
    "apartment", "villa", "duplex", "studio",
    "office", "shop", "gallery", "warehouse"
  ],
};

// Safety net for existing bad data: normalizes purpose values like "rental"/"sale"
// back to valid use-type purposes.
export function normalizePropertyPurpose(purpose: string | null, propertyType?: string): string {
  if (purpose && UNIT_TYPES_BY_PURPOSE[purpose]) return purpose;
  const typeMap: Record<string, string> = {
    residential: "residential",
    commercial: "commercial",
    industrial: "industrial",
    land: "agricultural",
    mixed: "multi_purpose",
  };
  if (propertyType && typeMap[propertyType]) return typeMap[propertyType];
  return "residential";
}

// Get unit types based on purpose only (for Add Unit form)
export function getUnitTypesForPurpose(purpose: string): string[] {
  return UNIT_TYPES_BY_PURPOSE[purpose] || [];
}

// Property types that are single units (property = unit)
export const SINGLE_UNIT_PROPERTY_TYPES = [
  "apartment", "villa", "studio", "office", "shop", "warehouse", "floor", 
  "room", "retreat", "farm", "factory", "power_plant", "telecom_tower", 
  "gas_station", "health_center", "school"
];

// Property types that can contain multiple units
export const MULTI_UNIT_PROPERTY_TYPES = ["building", "compound", "commercial_center", "land"];

// Matrix defining which unit types are valid for each property type
export const PROPERTY_UNIT_MATRIX: Record<string, Record<string, string[]>> = {
  agricultural: {
    land: ["agricultural_land"],
    farm: ["farm"],
  },
  residential: {
    land: ["residential_land"],
    floor: ["floor"],
    apartment: ["apartment"],
    villa: ["villa", "duplex", "floor", "palace"],
    studio: ["studio"],
    room: ["room"],
    retreat: ["retreat"],
    compound: ["villa", "apartment", "floor", "studio", "room"],
    building: ["floor", "apartment", "studio", "room"],
  },
  commercial: {
    building: ["office", "gallery", "booth", "warehouse", "cinema", "hotel", "car_parking", "workshop", "atm"],
    commercial_center: ["office", "gallery", "booth", "shop", "warehouse", "cinema", "hotel", "car_parking", "workshop", "atm"],
    land: ["commercial_land"],
  },
  industrial: {
    land: ["industrial_land"],
    factory: ["factory"],
    power_plant: ["power_plant"],
    telecom_tower: ["telecom_tower"],
    gas_station: ["gas_station"],
  },
  medical: {
    health_center: ["health_center"],
  },
  educational: {
    school: ["school"],
  },
  multi_purpose: {
    building: ["floor", "apartment", "studio", "room", "office", "gallery", "booth", "warehouse", "cinema", "hotel", "car_parking", "workshop", "atm"],
    compound: ["villa", "apartment", "floor", "room", "office", "shop", "booth", "warehouse"],
    commercial_center: ["office", "gallery", "booth", "shop", "warehouse", "cinema", "hotel", "car_parking", "workshop", "atm"],
    land: ["residential_land", "commercial_land", "agricultural_land"],
  },
};

// Helper functions
export function getPropertyTypesForPurpose(purpose: string): string[] {
  return PROPERTY_TYPES
    .filter(type => (type.purposes as readonly string[]).includes(purpose))
    .map(type => type.value);
}

export function getUnitTypesForPropertyType(purpose: string, propertyType: string): string[] {
  const matrix = PROPERTY_UNIT_MATRIX[purpose];
  if (!matrix) return [];
  
  return matrix[propertyType] || [];
}

export function isSingleUnitProperty(propertyType: string) {
  return SINGLE_UNIT_PROPERTY_TYPES.includes(propertyType);
}

export function isMultiUnitProperty(propertyType: string) {
  return MULTI_UNIT_PROPERTY_TYPES.includes(propertyType);
}

export function getPurposeLabel(purpose: string, language: Language) {
  const found = PROPERTY_PURPOSES.find(p => p.value === purpose);
  return language === "ar" ? found?.labelAr || purpose : found?.labelEn || purpose;
}

export function getPropertyTypeLabel(propertyType: string, language: Language) {
  const found = PROPERTY_TYPES.find(t => t.value === propertyType);
  return language === "ar" ? found?.labelAr || propertyType : found?.labelEn || propertyType;
}

export function getUnitTypeLabel(unitType: string, language: Language) {
  const found = UNIT_TYPES.find(t => t.value === unitType);
  return language === "ar" ? found?.labelAr || unitType : found?.labelEn || unitType;
}

// Display helper used by every unit picker. Returns "101 (Apartment)" when
// the unit has a type, falls back to just "101" for legacy rows where
// unit_type is null/undefined so the dropdown never shows an orphan "()".
// Single source of truth — change the format here and every picker
// updates.
export function getUnitDisplayLabel(
  unit: { unit_number: string; unit_type?: string | null },
  language: Language
): string {
  if (!unit.unit_type) return unit.unit_number;
  return `${unit.unit_number} (${getUnitTypeLabel(unit.unit_type, language)})`;
}

// Bilingual labels for a unit's occupancy status. Single source of truth —
// consumed by every unit-status badge (task card, project form, …). Callers
// resolve with t(UNIT_STATUS_LABELS[status].ar, UNIT_STATUS_LABELS[status].en).
export const UNIT_STATUS_LABELS: Record<string, { ar: string; en: string }> = {
  available: { ar: "متاح", en: "Available" },
  rented: { ar: "مؤجر", en: "Rented" },
  sold: { ar: "مباع", en: "Sold" },
  maintenance: { ar: "صيانة", en: "Maintenance" },
  reserved: { ar: "محجوز", en: "Reserved" },
};

// Derived type from the PROPERTY_TYPES matrix — used for CreatePropertyData
export const PROPERTY_TYPE_VALUES = PROPERTY_TYPES.map(t => t.value);
export type PropertyTypeValue = typeof PROPERTY_TYPES[number]["value"];
